export type ProfileImageKind = "avatar" | "photo";

const INPUT_LIMIT = 15 * 1024 * 1024;
const allowedTypes = new Set([
  "image/avif",
  "image/heic",
  "image/heif",
  "image/jpeg",
  "image/png",
  "image/webp",
]);

type DecodedImage = Readonly<{ height: number; source: CanvasImageSource; width: number; close: () => void }>;

export function profileImageDimensions(width: number, height: number, kind: ProfileImageKind): Readonly<{ height: number; width: number }> {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) throw new Error("Некорректное разрешение фотографии");
  const maxDimension = kind === "avatar" ? 512 : 1600;
  if (kind === "avatar") {
    const side = Math.max(1, Math.round(Math.min(maxDimension, width, height)));
    return { width: side, height: side };
  }
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

async function decode(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
  }
  const url = URL.createObjectURL(file);
  const image = new Image();
  try {
    image.src = url;
    await image.decode();
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error("Не удалось сжать фотографию")),
    "image/webp",
    quality,
  ));
}

export async function compressProfileImage(file: File, kind: ProfileImageKind): Promise<File> {
  if (!allowedTypes.has(file.type.toLowerCase())) throw new Error("Выберите JPEG, PNG, WebP, AVIF или HEIC");
  if (!file.size || file.size > INPUT_LIMIT) throw new Error("Исходная фотография должна занимать не более 15 МБ");
  let decoded: DecodedImage;
  try {
    decoded = await decode(file);
  } catch {
    // HEIC/AVIF могут не декодироваться конкретным WebView. Сервер всё равно проверит,
    // повернёт по EXIF, удалит метаданные и перекодирует такой файл в WebP.
    return file;
  }
  try {
    if (decoded.width * decoded.height > 40_000_000) throw new Error("Разрешение фотографии слишком большое");
    const dimensions = profileImageDimensions(decoded.width, decoded.height, kind);
    const canvas = document.createElement("canvas");
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return file;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    if (kind === "avatar") {
      const side = Math.min(decoded.width, decoded.height);
      context.drawImage(decoded.source, (decoded.width - side) / 2, (decoded.height - side) / 2, side, side, 0, 0, canvas.width, canvas.height);
    } else {
      context.drawImage(decoded.source, 0, 0, decoded.width, decoded.height, 0, 0, canvas.width, canvas.height);
    }
    const limit = kind === "avatar" ? 220 * 1024 : 680 * 1024;
    let best: Blob | null = null;
    for (const quality of [0.82, 0.76, 0.70, 0.64]) {
      const blob = await canvasBlob(canvas, quality);
      best = !best || blob.size < best.size ? blob : best;
      if (blob.size <= limit) break;
    }
    if (!best || best.size >= file.size) return file;
    const stem = file.name.replace(/\.[^.]+$/, "") || "profile";
    return new File([best], `${stem}.webp`, { type: "image/webp", lastModified: Date.now() });
  } finally {
    decoded.close();
  }
}
